import type { JSX } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";
import {
  allowRemoteContent,
  archiveMessages,
  canViewInline,
  closeEmlPreview,
  closeReader,
  copyToClipboard,
  deleteMessages,
  folderLabel,
  notSpamMessages,
  openCompose,
  openComposeFor,
  openEmlPreview,
  openAttachmentViewer,
  openMessage,
  openNeighbor,
  printMessage,
  setRemoteContentDefault,
  spamMessages,
  toggleFlagged,
  toggleSeen,
} from "../actions";
import {
  activeList,
  attachmentViewer,
  currentMailbox,
  emlPreview,
  emlPreviewError,
  emlPreviewLoading,
  fullMessage,
  mailboxes,
  messageError,
  messageLoading,
  mobilePane,
  movePicker,
  remoteContentDefault,
  selectedMailbox,
  selectedUid,
  showRemote,
  thread,
  userEmail,
} from "../state";
import { prefs } from "../prefs";
import { locale, t } from "../i18n";
import type { AttachmentMeta, Envelope, FullMessage, MailAddress } from "../../shared/types";
import { isEmlAttachment } from "../../shared/eml";
import {
  displayName,
  fileKind,
  formatBytes,
  formatFullDate,
  formatListDate,
  linkify,
  shouldShowRemoteBanner,
  type FileKind,
} from "../util";
import { Icon, type IconName } from "./icons";
import { api } from "../api";
import { Avatar, DESKTOP, Dialog, DialogHeader, Dropdown, EmptyState, MenuItem, Spinner, useMedia } from "./kit";

export function Reader(): JSX.Element {
  if (selectedUid.value == null) return <EmptyReader />;

  const m = fullMessage.value;
  const loading = messageLoading.value;

  return (
    <section class="flex min-h-0 min-w-0 flex-1 flex-col" aria-label={t("reader.label")}>
      <ReaderToolbar m={m} />
      {loading && !m && <ReaderSkeleton />}
      {messageError.value && !loading && (
        <div class="p-6">
          <div class="flex items-start gap-3 rounded-[10px] border border-danger/30 bg-danger/5 p-4 text-[13px]">
            <Icon name="alert" size={18} class="mt-0.5 shrink-0 text-danger" />
            <div class="flex-1">
              <p class="font-medium text-fg">{t("reader.loadError")}</p>
              <p class="mt-0.5 text-fg-3">{messageError.value}</p>
            </div>
          </div>
        </div>
      )}
      {m && <MessageView m={m} key={`${m.mailbox}:${m.uid}`} />}
    </section>
  );
}

/* --------------------------------- toolbar -------------------------------- */

function ReaderToolbar({ m }: { m: FullMessage | null }): JSX.Element {
  const desktop = useMedia(DESKTOP);
  const wide = useMedia("(min-width: 1100px)");
  const split = desktop && wide && prefs.value.readingPane === "right";
  const list = activeList.value;
  const openMbox = selectedMailbox.value ?? currentMailbox.value;
  const idx = list.findIndex((e) => e.uid === selectedUid.value && e.mailbox === openMbox);
  const use = mailboxes.value.find((b) => b.path === (m?.mailbox ?? openMbox))?.specialUse;
  const ref = m ? [{ uid: m.uid, mailbox: m.mailbox }] : [];

  return (
    <div class="flex h-14 shrink-0 items-center gap-0.5 border-b border-line px-2 md:px-3">
      {!split && (
        <button
          class="icon-btn mr-1"
          title={t("reader.back")}
          aria-label={t("reader.back")}
          onClick={() => {
            closeReader();
            mobilePane.value = "list";
          }}
        >
          <Icon name="arrowLeft" size={19} />
        </button>
      )}
      {use !== "archive" && (
        <ToolButton icon="archive" label={t("action.archive")} kbd="E" disabled={!m} onClick={() => void archiveMessages(ref)} />
      )}
      {use === "junk" ? (
        <ToolButton icon="inbox" label={t("action.notSpam")} disabled={!m} onClick={() => void notSpamMessages(ref)} />
      ) : (
        <ToolButton icon="spam" label={t("action.spam")} kbd="!" disabled={!m} onClick={() => void spamMessages(ref)} />
      )}
      <ToolButton icon="trash" label={t("action.delete")} kbd="#" disabled={!m} onClick={() => void deleteMessages(ref)} />
      <ToolButton
        icon="folderMove"
        label={t("action.move")}
        kbd="V"
        disabled={!m}
        onClick={() => m && (movePicker.value = { uids: [m.uid], mailbox: m.mailbox })}
      />
      <span class="mx-1 hidden h-5 w-px bg-line sm:block" />
      <ToolButton
        icon={m?.seen ? "mailUnread" : "mailOpen"}
        label={m?.seen ? t("action.markUnread") : t("action.markRead")}
        kbd="U"
        disabled={!m}
        class="hidden sm:inline-flex"
        onClick={() => m && void toggleSeen(m.uid, m.mailbox)}
      />

      <div class="ml-auto flex items-center gap-0.5">
        {idx >= 0 && list.length > 1 && (
          <>
            <span class="mr-1 hidden text-xs tabular-nums text-fg-3 lg:block">
              {t("reader.position", { i: idx + 1, n: list.length })}
            </span>
            <ToolButton icon="chevronUp" label={t("reader.newer")} kbd="K" disabled={idx <= 0} onClick={() => openNeighbor(-1)} />
            <ToolButton
              icon="chevronDown"
              label={t("reader.older")}
              kbd="J"
              disabled={idx >= list.length - 1}
              onClick={() => openNeighbor(1)}
            />
          </>
        )}
        <Dropdown
          trigger={(p) => (
            <button {...p} class="icon-btn" disabled={!m} title={t("common.more")} aria-label={t("common.more")}>
              <Icon name="dotsV" size={18} />
            </button>
          )}
        >
          {() =>
            m && (
              <>
                <MenuItem icon="reply" label={t("action.reply")} hint="R" onSelect={() => openComposeFor("reply")} />
                <MenuItem icon="replyAll" label={t("action.replyAll")} hint="A" onSelect={() => openComposeFor("replyAll")} />
                <MenuItem icon="forward" label={t("action.forward")} hint="F" onSelect={() => openComposeFor("forward")} />
                <div class="menu-sep" />
                <MenuItem
                  icon={m.flagged ? "starFilled" : "star"}
                  label={m.flagged ? t("action.unstar") : t("action.star")}
                  hint="S"
                  onSelect={() => void toggleFlagged(m.uid, m.mailbox)}
                />
                <MenuItem icon="printer" label={t("reader.print")} hint="P" onSelect={() => printMessage()} />
                <MenuItem
                  icon="code"
                  label={t("reader.source")}
                  onSelect={() => window.open(api.rawUrl(m.mailbox, m.uid), "_blank", "noopener")}
                />
                <MenuItem
                  icon="download"
                  label={t("reader.downloadEml")}
                  onSelect={() => {
                    const a = document.createElement("a");
                    a.href = api.rawUrl(m.mailbox, m.uid, true);
                    a.click();
                  }}
                />
                {m.from[0]?.address && (
                  <MenuItem
                    icon="copy"
                    label={t("reader.copySender")}
                    onSelect={() => void copyToClipboard(m.from[0]!.address)}
                  />
                )}
              </>
            )
          }
        </Dropdown>
      </div>
    </div>
  );
}

function ToolButton({
  icon,
  label,
  kbd,
  disabled,
  onClick,
  class: cls = "",
}: {
  icon: IconName;
  label: string;
  kbd?: string;
  disabled?: boolean;
  onClick: () => void;
  class?: string;
}): JSX.Element {
  return (
    <button
      class={`icon-btn ${cls}`}
      title={kbd ? `${label} (${kbd})` : label}
      aria-label={label}
      aria-keyshortcuts={kbd}
      disabled={disabled}
      onClick={onClick}
    >
      <Icon name={icon} size={18} />
    </button>
  );
}

/* --------------------------------- message -------------------------------- */

function MessageView({ m }: { m: FullMessage }): JSX.Element {
  const visibleAttachments = m.attachments.filter((a) => !a.inline);
  const crossFolder = m.mailbox !== currentMailbox.value;
  const others = m.to.length + m.cc.length;

  return (
    <div class="min-h-0 flex-1 overflow-y-auto">
      <article class="mx-auto max-w-[880px] px-4 pb-10 pt-6 md:px-8">
        <header>
          <div class="flex items-start gap-3">
            <h2 class="display-serif min-w-0 flex-1 text-[26px] font-medium leading-[1.2] text-fg md:text-[30px]">
              {m.subject || t("common.noSubject")}
            </h2>
            <button
              class="icon-btn mt-1"
              onClick={() => void toggleFlagged(m.uid, m.mailbox)}
              title={m.flagged ? t("action.unstar") : t("action.star")}
              aria-label={m.flagged ? t("action.unstar") : t("action.star")}
              aria-pressed={m.flagged}
            >
              <Icon name={m.flagged ? "starFilled" : "star"} size={20} class={m.flagged ? "text-star" : ""} />
            </button>
          </div>
          {crossFolder && (
            <span class="chip mt-2">
              <Icon name="folder" size={12} />
              {folderLabel(m.mailbox)}
            </span>
          )}
        </header>

        <ThreadStrip current={m} />

        <SenderBlock m={m} />

        <div class="mt-4 space-y-2">
          {shouldShowRemoteBanner(m.hasRemoteContent, showRemote.value, remoteContentDefault.value) && (
            <Banner icon="image">
              <span class="flex-1">{t("reader.remoteBlocked")}</span>
              <button class="btn btn-secondary btn-sm" onClick={() => allowRemoteContent()}>
                {t("reader.showImages")}
              </button>
              <button
                class="btn btn-ghost btn-sm hidden sm:inline-flex"
                onClick={() => {
                  void setRemoteContentDefault("always").then(() => allowRemoteContent());
                }}
              >
                {t("reader.alwaysShow")}
              </button>
            </Banner>
          )}
          {m.listUnsubscribe && (
            <Banner icon="mail">
              <span class="flex-1">{t("reader.mailingList")}</span>
              <UnsubscribeButton target={m.listUnsubscribe} />
            </Banner>
          )}
        </div>

        <div class="mt-5 overflow-hidden rounded-[var(--radius-sheet)] border border-line bg-sheet">
          {m.html != null ? <EmailFrame html={m.html} /> : <PlainBody text={m.text} />}
        </div>

        {visibleAttachments.length > 0 && <Attachments m={m} list={visibleAttachments} />}

        <div class="mt-8 flex flex-wrap gap-2">
          <button class="btn btn-secondary" onClick={() => openComposeFor("reply")}>
            <Icon name="reply" size={16} />
            {t("action.reply")}
          </button>
          {others > 1 && (
            <button class="btn btn-secondary" onClick={() => openComposeFor("replyAll")}>
              <Icon name="replyAll" size={16} />
              {t("action.replyAll")}
            </button>
          )}
          <button class="btn btn-secondary" onClick={() => openComposeFor("forward")}>
            <Icon name="forward" size={16} />
            {t("action.forward")}
          </button>
        </div>
      </article>
    </div>
  );
}

function Banner({ icon, children }: { icon: IconName; children: JSX.Element | JSX.Element[] | (JSX.Element | false)[] }): JSX.Element {
  return (
    <div class="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-[10px] border border-line bg-canvas px-3.5 py-2.5 text-[13px] text-fg-2">
      <Icon name={icon} size={16} class="shrink-0 text-fg-3" />
      {children}
    </div>
  );
}

function UnsubscribeButton({ target }: { target: string }): JSX.Element | null {
  if (target.startsWith("mailto:")) {
    const [addr, query = ""] = target.slice(7).split("?");
    const subject = new URLSearchParams(query).get("subject") ?? "unsubscribe";
    return (
      <button class="btn btn-secondary btn-sm" onClick={() => openCompose({ to: decodeURIComponent(addr ?? ""), subject })}>
        {t("reader.unsubscribe")}
      </button>
    );
  }
  // The server only emits https/mailto, but never trust a URL scheme in an href.
  let url: URL;
  try {
    url = new URL(target);
  } catch {
    return null;
  }
  if (url.protocol !== "https:") return null;
  return (
    <a class="btn btn-secondary btn-sm" href={url.href} target="_blank" rel="noopener noreferrer">
      {t("reader.unsubscribe")}
      <Icon name="external" size={13} />
    </a>
  );
}

function recipientSummary(m: FullMessage): string {
  const me = userEmail.value.toLowerCase();
  const all = [...m.to, ...m.cc];
  const names = all.map((a) => (a.address.toLowerCase() === me ? t("reader.me") : displayName(a)));
  if (names.length === 0) return t("reader.undisclosed");
  const head = names.slice(0, 2).join(", ");
  return names.length > 2 ? t("reader.andMore", { list: head, n: names.length - 2 }) : head;
}

function SenderBlock({ m }: { m: FullMessage }): JSX.Element {
  const [details, setDetails] = useState(false);
  const from = m.from[0];
  const name = displayName(from);
  return (
    <div class="mt-5 flex items-start gap-3">
      <Avatar address={from?.address ?? ""} label={name} size={40} />
      <div class="min-w-0 flex-1">
        <div class="flex flex-wrap items-baseline gap-x-2">
          <span class="truncate text-[14px] font-semibold text-fg">{name}</span>
          {from?.address && from.address !== name && <span class="truncate text-[13px] text-fg-3">{from.address}</span>}
        </div>
        <button
          class="mt-0.5 flex max-w-full items-center gap-1 text-[12.5px] text-fg-3 hover:text-fg"
          onClick={() => setDetails(!details)}
          aria-expanded={details}
        >
          <span class="truncate">
            {t("reader.toLabel")} {recipientSummary(m)}
          </span>
          <Icon name={details ? "chevronUp" : "chevronDown"} size={13} class="shrink-0" />
        </button>
        {details && (
          <dl class="animate-fade mt-2.5 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 rounded-[10px] bg-canvas p-3 text-[12.5px]">
            <AddrRow label={t("compose.from")} list={m.from} />
            {m.replyTo.length > 0 && m.replyTo[0]?.address !== m.from[0]?.address && (
              <AddrRow label={t("reader.replyTo")} list={m.replyTo} />
            )}
            <AddrRow label={t("compose.to")} list={m.to} />
            {m.cc.length > 0 && <AddrRow label="Cc" list={m.cc} />}
            {m.bcc.length > 0 && <AddrRow label="Bcc" list={m.bcc} />}
            <dt class="text-fg-3">{t("compose.date")}</dt>
            <dd class="text-fg-2">{formatFullDate(m.date, locale.value)}</dd>
          </dl>
        )}
      </div>
      <time class="shrink-0 pt-0.5 text-xs text-fg-3" dateTime={m.date} title={formatFullDate(m.date, locale.value)}>
        {formatListDate(m.date, locale.value, t("date.yesterday"))}
      </time>
    </div>
  );
}

function AddrRow({ label, list }: { label: string; list: MailAddress[] }): JSX.Element {
  return (
    <>
      <dt class="text-fg-3">{label}</dt>
      <dd class="min-w-0 space-y-0.5 text-fg-2">
        {list.length === 0
          ? "—"
          : list.map((a) => (
              <div key={a.address} class="truncate">
                {a.name ? (
                  <>
                    <span class="text-fg">{a.name}</span> <span class="text-fg-3">&lt;{a.address}&gt;</span>
                  </>
                ) : (
                  a.address
                )}
              </div>
            ))}
      </dd>
    </>
  );
}

/** Other messages of the conversation, collapsed by default. */
function ThreadStrip({ current }: { current: FullMessage }): JSX.Element | null {
  const [open, setOpen] = useState(false);
  const list = thread.value;
  if (list.length < 2) return null;
  return (
    <div class="mt-4 rounded-[10px] border border-line">
      <button
        class="flex w-full items-center gap-2 px-3.5 py-2.5 text-left text-[13px] text-fg-2 hover:text-fg"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
      >
        <Icon name="replyAll" size={15} class="text-fg-3" />
        <span class="flex-1">{t("reader.thread", { n: list.length })}</span>
        <Icon name={open ? "chevronUp" : "chevronDown"} size={14} />
      </button>
      {open && (
        <ol class="border-t border-line">
          {list.map((e: Envelope) => {
            const isCurrent = e.uid === current.uid && e.mailbox === current.mailbox;
            return (
              <li key={`${e.mailbox}:${e.uid}`}>
                <button
                  disabled={isCurrent}
                  onClick={() => void openMessage(e.uid, e.mailbox)}
                  class={`flex w-full items-center gap-3 px-3.5 py-2 text-left text-[13px] ${isCurrent ? "bg-accent/6" : "hover:bg-raised"}`}
                >
                  <span class={`h-1.5 w-1.5 shrink-0 rounded-full ${isCurrent ? "bg-accent" : e.seen ? "bg-transparent" : "bg-accent/60"}`} />
                  <span class={`w-36 shrink-0 truncate ${isCurrent ? "font-semibold text-fg" : "text-fg-2"}`}>{displayName(e.from[0])}</span>
                  <span class="min-w-0 flex-1 truncate text-fg-3">{e.preview || e.subject}</span>
                  <span class="shrink-0 text-xs text-fg-3">{formatListDate(e.date, locale.value, t("date.yesterday"))}</span>
                </button>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}

function PlainBody({ text }: { text: string | null }): JSX.Element {
  if (!text) return <p class="p-6 text-[14px] italic text-neutral-500">{t("reader.empty")}</p>;
  return (
    <div class="whitespace-pre-wrap break-words p-6 text-[14px] leading-relaxed text-neutral-900">
      {linkify(text).map((seg, i) =>
        seg.href ? (
          <a key={i} href={seg.href} target="_blank" rel="noopener noreferrer" class="text-blue-700 underline">
            {seg.text}
          </a>
        ) : (
          seg.text
        ),
      )}
    </div>
  );
}

/**
 * Renders sanitized email HTML inside a sandboxed iframe.
 * `allow-same-origin` (WITHOUT `allow-scripts`) lets us measure content height
 * for auto-fit while keeping all scripts disabled — a safe display sandbox.
 */
function EmailFrame({ html }: { html: string }): JSX.Element {
  const ref = useRef<HTMLIFrameElement>(null);
  const fit = (): void => {
    const f = ref.current;
    if (!f) return;
    try {
      const doc = f.contentDocument;
      if (doc?.body) {
        // No cap — let the iframe grow to its full content height.
        f.style.height = `${doc.documentElement.scrollHeight}px`;
      }
    } catch {
      /* cross-origin guard; ignore */
    }
  };

  // Late-loading images and fonts change the height after `load`; keep fitting.
  useEffect(() => {
    const f = ref.current;
    if (!f) return;
    let ro: ResizeObserver | null = null;
    const attach = (): void => {
      try {
        const body = f.contentDocument?.body;
        if (body && typeof ResizeObserver !== "undefined") {
          ro = new ResizeObserver(fit);
          ro.observe(body);
        }
      } catch {
        /* ignore */
      }
    };
    f.addEventListener("load", attach);
    return () => {
      f.removeEventListener("load", attach);
      ro?.disconnect();
    };
  }, [html]);

  // Scoped CSP for the sandboxed document itself. `srcdoc` has no CSP of its
  // own by default, so without this it INHERITS the app shell's CSP (per the
  // CSP inheritance rules for local-scheme documents) — whose `img-src`
  // only allows `https:`, silently blocking plain-`http:` remote images even
  // after the user clicks "Show content" and the server restores their `src`.
  // The real security boundary here is the sandbox (no allow-scripts) plus
  // server-side sanitizeEmailHtml, not this CSP, so it's safe to allow image
  // loads broadly, scoped ONLY to this isolated document.
  const srcdoc = `<!doctype html><html><head><meta name="color-scheme" content="light"><meta http-equiv="Content-Security-Policy" content="default-src 'none'; img-src * data: blob:; style-src 'unsafe-inline'; font-src * data:; script-src 'none'; object-src 'none'; base-uri 'none'; form-action 'none';"><base target="_blank"><style>html,body{margin:0}body{padding:24px;font-family:ui-sans-serif,system-ui,-apple-system,"Segoe UI",sans-serif;color:#1a1a1a;font-size:14px;line-height:1.55;word-break:break-word;overflow-wrap:anywhere}img{max-width:100%;height:auto}table{max-width:100%}pre{white-space:pre-wrap}blockquote{margin:0 0 0 4px;border-left:2px solid #d0d4dc;padding-left:12px;color:#555}a{color:#1d4ed8}</style></head><body>${html}</body></html>`;
  return (
    <iframe
      ref={ref}
      onLoad={fit}
      title={t("reader.body")}
      sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
      referrerpolicy="no-referrer"
      srcdoc={srcdoc}
      class="block w-full bg-sheet"
      style="min-height:120px;border:0;"
      scrolling="no"
    />
  );
}

/* ------------------------------- attachments ------------------------------ */

const KIND_STYLE: Record<FileKind, { icon: IconName; tint: string }> = {
  image: { icon: "image", tint: "#0e9f6e" },
  pdf: { icon: "fileText", tint: "#d93a3a" },
  doc: { icon: "fileText", tint: "#2563eb" },
  sheet: { icon: "fileText", tint: "#15803d" },
  slides: { icon: "fileText", tint: "#d97706" },
  archive: { icon: "fileArchive", tint: "#7c3aed" },
  audio: { icon: "music", tint: "#db2777" },
  video: { icon: "video", tint: "#9333ea" },
  mail: { icon: "mail", tint: "#0891b2" },
  calendar: { icon: "calendar", tint: "#ea580c" },
  code: { icon: "code", tint: "#475569" },
  file: { icon: "file", tint: "#64748b" },
};

function Attachments({ m, list }: { m: FullMessage; list: AttachmentMeta[] }): JSX.Element {
  const total = list.reduce((n, a) => n + a.size, 0);
  return (
    <section class="mt-6" aria-label={t("reader.attachments")}>
      <h3 class="mb-2.5 flex items-center gap-2 text-[13px] font-medium text-fg-2">
        <Icon name="paperclip" size={15} class="text-fg-3" />
        {t("reader.attachmentsCount", { n: list.length })}
        {total > 0 && <span class="font-normal text-fg-3">{formatBytes(total)}</span>}
      </h3>
      <div class="grid grid-cols-[repeat(auto-fill,minmax(230px,1fr))] gap-2">
        {list.map((a) => {
          const kind = fileKind(a.contentType, a.filename);
          const style = KIND_STYLE[kind];
          const eml = isEmlAttachment(a.contentType, a.filename);
          const viewable = canViewInline(a.contentType);
          const view = eml
            ? () => void openEmlPreview(a.partId)
            : viewable
              ? () => openAttachmentViewer(a.partId, a.filename, a.contentType)
              : null;
          return (
            <div key={a.partId} class="group flex items-center gap-3 rounded-[10px] border border-line bg-surface p-2.5 transition-colors hover:border-line-strong">
              {kind === "image" ? (
                <button
                  class="h-10 w-10 shrink-0 overflow-hidden rounded-[7px] bg-raised"
                  onClick={view ?? undefined}
                  aria-label={t("reader.view")}
                >
                  <img
                    src={api.attachmentUrl(m.mailbox, m.uid, a.partId, true)}
                    alt=""
                    loading="lazy"
                    class="h-full w-full object-cover"
                  />
                </button>
              ) : (
                <span
                  class="flex h-10 w-10 shrink-0 items-center justify-center rounded-[7px]"
                  style={`background:color-mix(in srgb, ${style.tint} 14%, transparent);color:${style.tint}`}
                >
                  <Icon name={style.icon} size={19} />
                </span>
              )}
              <button
                class="min-w-0 flex-1 text-left"
                onClick={view ?? undefined}
                disabled={!view}
                title={a.filename}
              >
                <span class="block truncate text-[13px] font-medium text-fg">{a.filename}</span>
                <span class="block text-xs text-fg-3">{formatBytes(a.size) || a.contentType}</span>
              </button>
              {view && (
                <button class="icon-btn icon-btn-sm" title={t("reader.view")} aria-label={t("reader.view")} onClick={view}>
                  <Icon name="eye" size={16} />
                </button>
              )}
              <a
                class="icon-btn icon-btn-sm"
                href={api.attachmentUrl(m.mailbox, m.uid, a.partId)}
                download={a.filename}
                title={t("reader.download")}
                aria-label={`${t("reader.download")} ${a.filename}`}
              >
                <Icon name="download" size={16} />
              </a>
            </div>
          );
        })}
      </div>
    </section>
  );
}

/** Full-screen viewer for images, PDFs and plain-text attachments. */
export function AttachmentViewerModal(): JSX.Element | null {
  const v = attachmentViewer.value;
  if (!v) return null;
  const close = (): void => {
    attachmentViewer.value = null;
  };
  const isImage = v.contentType.startsWith("image/");
  return (
    <Dialog onClose={close} label={v.filename} class="flex h-[88vh] max-w-5xl flex-col">
      <div class="flex shrink-0 items-center gap-3 border-b border-line px-4 py-2.5">
        <span class="min-w-0 flex-1 truncate text-[14px] font-medium text-fg">{v.filename}</span>
        <a class="btn btn-secondary btn-sm" href={v.downloadUrl} download={v.filename}>
          <Icon name="download" size={14} />
          {t("reader.download")}
        </a>
        <a class="icon-btn" href={v.url} target="_blank" rel="noopener" title={t("reader.openTab")} aria-label={t("reader.openTab")}>
          <Icon name="external" size={17} />
        </a>
        <button class="icon-btn" data-close onClick={close} aria-label={t("common.close")}>
          <Icon name="x" size={18} />
        </button>
      </div>
      <div class="flex min-h-0 flex-1 items-center justify-center bg-canvas">
        {isImage ? (
          <img src={v.url} alt={v.filename} class="max-h-full max-w-full object-contain p-4" />
        ) : (
          <iframe src={v.url} title={v.filename} class="h-full w-full border-0 bg-sheet" sandbox={v.contentType === "application/pdf" ? undefined : ""} />
        )}
      </div>
    </Dialog>
  );
}

/**
 * Modal preview of a nested `message/rfc822`/`.eml` attachment. Signal-driven:
 * open whenever a preview is loading, loaded, or errored; mounted globally in
 * `App`. Reuses the file-local {@link EmailFrame} so the nested body renders in
 * the exact same sandboxed, server-sanitized iframe as top-level messages —
 * there is no second HTML render path. Nested attachments are listed by name
 * only (no download/view — no recursion).
 */
export function EmlPreviewModal(): JSX.Element | null {
  const preview = emlPreview.value;
  const loading = emlPreviewLoading.value;
  const err = emlPreviewError.value;

  // Closed: nothing loading, loaded, or errored.
  if (!preview && !loading && !err) return null;

  return (
    <Dialog onClose={closeEmlPreview} label={t("eml.title")} class="flex max-h-[88vh] max-w-3xl flex-col">
      <DialogHeader
        title={preview ? preview.subject || t("common.noSubject") : t("eml.title")}
        sub={t("eml.sub")}
        onClose={closeEmlPreview}
      />
      <div class="min-h-0 flex-1 overflow-y-auto">
        {loading && (
          <div class="flex items-center gap-3 p-6 text-[13px] text-fg-3">
            <Spinner /> {t("eml.loading")}
          </div>
        )}
        {err && !loading && (
          <div class="flex items-center gap-2 p-6 text-[13px] text-danger">
            <Icon name="alert" size={16} />
            <span>{err}</span>
          </div>
        )}
        {preview && !loading && !err && (
          <div class="p-5">
            <dl class="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-[12.5px]">
              <AddrRow label={t("compose.from")} list={preview.from} />
              <AddrRow label={t("compose.to")} list={preview.to} />
              {preview.cc.length > 0 && <AddrRow label="Cc" list={preview.cc} />}
              <dt class="text-fg-3">{t("compose.date")}</dt>
              <dd class="text-fg-2">{formatFullDate(preview.date, locale.value)}</dd>
            </dl>
            <div class="mt-4 overflow-hidden rounded-[var(--radius-sheet)] border border-line bg-sheet">
              {preview.html != null ? <EmailFrame html={preview.html} /> : <PlainBody text={preview.text} />}
            </div>
            {preview.attachments.length > 0 && (
              <div class="mt-4">
                <div class="mb-2 text-xs font-medium text-fg-3">{t("eml.nested", { n: preview.attachments.length })}</div>
                <ul class="space-y-1">
                  {preview.attachments.map((att, i) => (
                    <li key={`${att.filename}-${i}`} class="flex items-center gap-1.5 text-xs text-fg-2">
                      <Icon name="paperclip" size={13} class="text-fg-3" />
                      <span class="max-w-[20rem] truncate">{att.filename || "(unnamed)"}</span>
                      {att.size > 0 && <span class="text-fg-3">({formatBytes(att.size)})</span>}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </Dialog>
  );
}

function EmptyReader(): JSX.Element {
  return (
    <section class="flex min-w-0 flex-1 items-center justify-center bg-canvas/40">
      <EmptyState
        icon="mailOpen"
        title={t("reader.emptyTitle")}
        body={t("reader.emptyBody")}
        action={
          <div class="flex items-center gap-4 text-xs text-fg-3">
            <span class="flex items-center gap-1.5">
              <kbd class="kbd">J</kbd>
              <kbd class="kbd">K</kbd> {t("reader.hintNavigate")}
            </span>
            <span class="flex items-center gap-1.5">
              <kbd class="kbd">C</kbd> {t("reader.hintCompose")}
            </span>
            <span class="flex items-center gap-1.5">
              <kbd class="kbd">?</kbd> {t("reader.hintHelp")}
            </span>
          </div>
        }
      />
    </section>
  );
}

function ReaderSkeleton(): JSX.Element {
  return (
    <div class="mx-auto w-full max-w-[880px] space-y-5 px-4 pt-6 md:px-8" aria-hidden="true">
      <div class="skeleton h-7 w-3/4" />
      <div class="flex items-center gap-3">
        <div class="skeleton h-10 w-10 shrink-0 !rounded-full" />
        <div class="flex-1 space-y-2">
          <div class="skeleton h-3 w-1/3" />
          <div class="skeleton h-3 w-1/4" />
        </div>
      </div>
      <div class="skeleton h-64 w-full !rounded-[var(--radius-sheet)]" />
    </div>
  );
}
